import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";
import Comparison from "@/components/home/Comparison";
import WaitlistCTA from "@/components/home/WaitlistCTA";

const TIER_PRICES = ["0.9%", "0.5%", "0.7%"];
const TIER_ACCENT = [false, true, false];
const TIER_LIMITED = [false, true, false];

export default function Fees() {
  const { t } = useTranslation();
  const TIERS = (t("content.fees.tiers", { returnObjects: true }) || []).map((tier, i) => ({
    name: tier.name,
    price: TIER_PRICES[i] || "—",
    priceNote: tier.priceNote,
    features: tier.features || [],
    cta: tier.cta,
    accent: TIER_ACCENT[i] || false,
    limited: TIER_LIMITED[i] || false,
  }));
  return (
    <>
      <SEO
        slug="fees"
        title="Fees · 0.9% flat, nothing hidden — Instantxfer CashApp"
        description="No spread, no wire fee, no membership tier. Instantxfer CashApp takes 0.9% flat and shows the number to the cent before you tap send."
      />
      <PageHero
        eyebrow={t("pageHero.fees.eyebrow")}
        title={t("pageHero.fees.title")}
        italic={t("pageHero.fees.italic")}
        body={t("pageHero.fees.body")}
        testId="fees-hero"
      />

      <section className="pb-24 bg-canvas">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={`fee-tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`card-flat p-8 md:p-10 flex flex-col ${
                t.accent ? "!bg-forest text-canvas !border-forest" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className={`font-display text-xl ${t.accent ? "text-canvas" : "text-forest"}`}>
                  {t.name}
                </div>
                {t.limited && (
                  <span className="text-[10px] tracking-[0.22em] uppercase bg-lime text-ink px-2.5 py-1 rounded-full font-semibold">
                    Limited
                  </span>
                )}
              </div>
              <div className="mt-6">
                <div className={`font-display text-6xl tracking-tightest ${t.accent ? "text-lime" : "text-forest"}`}>
                  {t.price}
                </div>
                <div className={`mt-1 text-sm ${t.accent ? "text-canvas/60" : "text-ink/60"}`}>
                  {t.priceNote}
                </div>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm ${t.accent ? "text-canvas/85" : "text-ink/70"}`}>
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${t.accent ? "bg-lime" : "bg-forest"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={t.name === "Business" ? "/contact" : "/waitlist"}
                className={`mt-8 justify-center ${t.accent ? "btn-accent" : "btn-primary"}`}
                data-testid={`fee-cta-${t.name.toLowerCase()}`}
              >
                {t.cta}
              </a>
            </div>
          ))}
        </div>

        <div className="container-x mt-16">
          <div className="hairline mb-10" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-ink/70">
            <div>
              <div className="label-eyebrow">Fine print, honestly</div>
              <p className="mt-3 leading-relaxed">
                We charge the 0.9% on the send amount, calculated at mid-market. That&apos;s it. No wire
                fees, no correspondent bank fees, no monthly maintenance.
              </p>
            </div>
            <div>
              <div className="label-eyebrow">FX spread</div>
              <p className="mt-3 leading-relaxed">
                Zero. We stream mid-market from three major sources and settle at that rate. Locked
                quotes hold for 60 seconds.
              </p>
            </div>
            <div>
              <div className="label-eyebrow">Cash pickup</div>
              <p className="mt-3 leading-relaxed">
                Some corridors carry an agent fee ($1–$3), which we surface up-front on the ticket.
                Mobile money and bank payouts are always free of hidden charges.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Comparison />
      <WaitlistCTA />
    </>
  );
}
